import { ReactNode, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { CirclePlusIcon, XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, LocationInput, Row } from "@/components";

import { SheetResourcesControls } from "./SheetResourcesControls";
import { SheetResourcesWrapper } from "./SheetResourcesWrapper";
import { Education } from "@/components/educations/Education";
import { EducationsView } from "@/components/educations/EducationsView";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useEducation } from "@/hooks/mutations/useEducation";
import { monthOptions } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { locations } from "@/lib/locations";
import { EducationType } from "@/types";

const educationsTitle = "Education";

const educationSchema = z
  .object({
    school: z.string().min(1, { message: "Required" }),
    degree: z.string(),
    location: z
      .string()
      .refine(
        (location) =>
          locations.validateLocation({ enforceFullLocation: false, location }),
        {
          message:
            "Must be City, State, USA with a valid state code (e.g Knoxville, TN, USA)",
        },
      ),
    startMonth: z.string(),
    startYear: z.string().refine((data) => !isNaN(Number(data)), {
      message: "Year must be a valid integer",
    }),
    endMonth: z.string(),
    endYear: z.string().refine((data) => !isNaN(Number(data)), {
      message: "Year must be a valid integer",
    }),
  })
  .superRefine((data, ctx) => {
    dates.validateDatesForForm(data, ctx, false);
  });

const EducationForm = ({
  form,
}: {
  form: ReturnType<typeof useForm<z.infer<typeof educationSchema>>>;
}) => {
  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4 [&_label]:font-plex-mono [&_label]:text-[11px] [&_label]:font-medium [&_label]:uppercase [&_label]:tracking-wider [&_label]:text-slate-400"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Row className="flex-wrap items-start gap-4">
          <FormField
            control={form.control}
            name="school"
            render={({ field }) => (
              <FormItem className="min-w-40 flex-1 text-slate-700">
                <FormLabel>School</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="degree"
            render={({ field }) => (
              <FormItem className="min-w-40 flex-1 text-slate-700">
                <FormLabel>Degree</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Row>
        <LocationInput fieldName="location" formControl={form.control} />
        <Row className="flex-wrap items-start gap-4">
          <FormField
            control={form.control}
            name="startMonth"
            render={({ field }) => (
              <FormItem className="min-w-40 flex-1 text-slate-700">
                <FormLabel>Start Month</FormLabel>
                <Select
                  defaultValue={field.value}
                  onValueChange={field.onChange}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="text-slate-700">
                    {monthOptions.map((monthOption) => (
                      <SelectItem
                        key={monthOption.value}
                        value={monthOption.value}
                      >
                        {monthOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="startYear"
            render={({ field }) => (
              <FormItem className="min-w-40 flex-1 text-slate-700">
                <FormLabel>Start Year</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Row>
        <Row className="flex-wrap items-start gap-4">
          <FormField
            control={form.control}
            name="endMonth"
            render={({ field }) => (
              <FormItem className="min-w-40 flex-1 text-slate-700">
                <FormLabel>End Month</FormLabel>
                <Select
                  defaultValue={field.value}
                  onValueChange={field.onChange}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="text-slate-700">
                    {monthOptions.map((monthOption) => (
                      <SelectItem
                        key={monthOption.value}
                        value={monthOption.value}
                      >
                        {monthOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endYear"
            render={({ field }) => (
              <FormItem className="min-w-40 flex-1 text-slate-700">
                <FormLabel>End Year</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Row>
      </form>
    </Form>
  );
};

const EducationAdd = ({
  handleCancel,
  userId,
}: {
  handleCancel: () => void;
  userId: number;
}) => {
  const { createEducation } = useEducation();

  const disabled = createEducation.isPending;

  const form = useForm<z.infer<typeof educationSchema>>({
    defaultValues: {
      school: "",
      degree: "",
      location: "",
      startMonth: "",
      startYear: "",
      endMonth: "",
      endYear: "",
    },
    mode: "onChange",
    resolver: zodResolver(educationSchema),
  });

  const handleCreateEducation = (data: z.infer<typeof educationSchema>) => {
    const createRequest = {
      userId,
      school: data.school,
      degree: data.degree || undefined,
      location: data.location || undefined,
      startMonth: data.startMonth || undefined,
      startYear: data.startYear || undefined,
      endMonth: data.endMonth || undefined,
      endYear: data.endYear || undefined,
      current: !!(
        data.startMonth &&
        data.startYear &&
        !data.endMonth &&
        !data.endYear
      ),
    };

    // useEducation updates the sheet-data cache at the hook level.
    createEducation.mutate(
      {
        userId,
        data: createRequest,
      },
      {
        onSuccess: () => {
          form.reset();
          handleCancel();
        },
      },
    );
  };

  const handleSave = () => {
    void form.handleSubmit(handleCreateEducation)();
  };

  return (
    <Column className="items-end">
      <Button
        className="h-8 w-8"
        disabled={disabled}
        onClick={handleCancel}
        size="icon"
        type="button"
        variant="ghost"
      >
        <XIcon className="h-4 w-4 text-slate-600" />
      </Button>
      <Column className="w-full">
        <EducationForm form={form} />
      </Column>
      <Row className="items-center justify-end pb-6 pt-4">
        <Button
          disabled={disabled || !form.formState.isValid}
          onClick={handleSave}
          size="sm"
          type="button"
        >
          Done
        </Button>
      </Row>
    </Column>
  );
};

const EducationEdit = ({
  education,
  userId,
}: {
  education: EducationType;
  userId: number;
}) => {
  const [isShowingUpdateForm, setIsShowingUpdateForm] = useState(false);

  const { deleteEducation, updateEducation } = useEducation();

  const disabled = deleteEducation.isPending || updateEducation.isPending;

  const form = useForm<z.infer<typeof educationSchema>>({
    defaultValues: {
      school: education.school ?? "",
      degree: education.degree ?? "",
      location: education.location ?? "",
      startMonth: education.startMonth ?? "",
      startYear: education.startYear ?? "",
      endMonth: education.endMonth ?? "",
      endYear: education.endYear ?? "",
    },
    mode: "onChange",
    resolver: zodResolver(educationSchema),
  });

  // useEducation updates the sheet-data cache at the hook level, so success
  // handlers here only manage local form state.
  const handleDeleteEducation = () => {
    deleteEducation.mutate({ id: education.id, userId });
  };

  const handleUpdateEducation = (data: z.infer<typeof educationSchema>) => {
    const updateRequest = {
      id: education.id,
      userId,
      school: data.school,
      degree: data.degree,
      location: data.location,
      startMonth: data.startMonth,
      startYear: data.startYear,
      endMonth: data.endMonth,
      endYear: data.endYear,
      current: !!(
        data.startMonth &&
        data.startYear &&
        !data.endMonth &&
        !data.endYear
      ),
    };

    updateEducation.mutate(
      {
        userId,
        data: updateRequest,
      },
      {
        onSuccess: (updatedEducation) => {
          handleUpdateEducationSuccess(updatedEducation as EducationType);
        },
      },
    );
  };

  const handleUpdateEducationSuccess = (updatedEducation: EducationType) => {
    form.reset({
      school: updatedEducation.school ?? "",
      degree: updatedEducation.degree ?? "",
      location: updatedEducation.location ?? "",
      startMonth: updatedEducation.startMonth ?? "",
      startYear: updatedEducation.startYear ?? "",
      endMonth: updatedEducation.endMonth ?? "",
      endYear: updatedEducation.endYear ?? "",
    });
    setIsShowingUpdateForm(false);
  };

  const handleCancel = () => {
    form.reset();
    setIsShowingUpdateForm(false);
  };

  const handleEdit = () => {
    setIsShowingUpdateForm(true);
  };

  const handleSave = () => {
    if (disabled || !form.formState.isValid) return;

    void form.handleSubmit(handleUpdateEducation)();
  };

  if (isShowingUpdateForm) {
    return (
      <Column className="gap-0">
        <EducationForm form={form} />
        <Row className="items-center justify-end pb-6 pt-4">
          <Button
            disabled={disabled || !form.formState.isValid}
            onClick={handleSave}
            size="sm"
            type="button"
          >
            Done
          </Button>
        </Row>
      </Column>
    );
  }

  return (
    <Row
      className="group/education w-full items-start gap-2"
      onDoubleClick={handleEdit}
    >
      <Column className="min-w-0 flex-1">
        <Education education={education} />
      </Column>
      <Column className="opacity-0 transition-opacity duration-150 group-hover/education:opacity-100 group-hover/education:delay-200">
        <SheetResourcesControls
          disabled={disabled}
          isEditing={false}
          onCancel={handleCancel}
          onDelete={handleDeleteEducation}
          onEdit={handleEdit}
          onSave={handleSave}
        />
      </Column>
    </Row>
  );
};

const EducationsEdit = ({
  educations,
  isShowingAddForm,
  setIsShowingAddForm,
  userId,
}: {
  educations: EducationType[];
  isShowingAddForm: boolean;
  setIsShowingAddForm: (isShowing: boolean) => void;
  userId: number;
}) => {
  return (
    <Column className="gap-4">
      {isShowingAddForm ? (
        <EducationAdd
          handleCancel={() => {
            setIsShowingAddForm(false);
          }}
          userId={userId}
        />
      ) : null}
      {educations.map((education) => (
        <EducationEdit
          key={education.id}
          education={education}
          userId={userId}
        />
      ))}
    </Column>
  );
};

export const SheetEducations = ({
  educations,
  titleActions,
  userId,
}: {
  educations: EducationType[];
  titleActions?: ReactNode;
  // Present when the profile is editable.
  userId?: number;
}) => {
  // Filter out educations without school and degree.
  const filteredEducations = educations.filter(
    (education) => !!education.degree?.trim() || !!education.school?.trim(),
  );

  const [isShowingAddForm, setIsShowingAddForm] = useState(false);

  // Editable profiles get per-row edit/delete controls plus a header add
  // button — no section-wide edit mode.
  if (userId !== undefined) {
    return (
      <SheetResourcesWrapper
        isEmpty={!filteredEducations.length}
        title={educationsTitle}
        titleActions={
          isShowingAddForm ? undefined : (
            <Button
              className="h-6 w-6 opacity-0 transition-opacity duration-150 group-hover/section:opacity-100 group-hover/section:delay-200"
              onClick={() => {
                setIsShowingAddForm(true);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <CirclePlusIcon className="h-3.5 w-3.5 text-slate-500" />
            </Button>
          )
        }
      >
        <Column className="gap-4">
          {/* Hide the empty-state text while the add form is open. */}
          {!filteredEducations.length && !isShowingAddForm ? (
            <EducationsView educations={filteredEducations} />
          ) : null}
          <EducationsEdit
            educations={filteredEducations}
            isShowingAddForm={isShowingAddForm}
            setIsShowingAddForm={setIsShowingAddForm}
            userId={userId}
          />
        </Column>
      </SheetResourcesWrapper>
    );
  }

  return (
    <SheetResourcesWrapper
      isEmpty={!filteredEducations.length}
      title={educationsTitle}
      titleActions={titleActions}
    >
      <EducationsView educations={filteredEducations} />
    </SheetResourcesWrapper>
  );
};
